﻿// This component will be used on the pages typescript files and will execute the scripts tht should fire on every page e.g. footer, header, etc
import {logErrorEvent} from "../services-modules/logger-service";
import {getCookie, writeCookie} from "../utils_modules/cookie";
import {expressQuerySelector, expressQuerySelectorAll} from "../common/html";
import {createCollapsable} from "./collapsable";
import {createNewsletterSubscribeAndUnsubscribe} from "./newsletter";
import {createMainNavigation} from "./main-navigation";
import {getInlineSvgsAsync} from "../utils_modules/icons";
import {createCountrySelection} from "./country-selection";
import {createPopup} from "./popup-new";
import {
	canNotContainOnlyNumerics,
	isValidEmailAddress,
	isValidTextWithoutAngleBrackets,
	isValidWithoutNonAlphaNumericCharsAtEnd
} from "../utils_modules/validation";
import {checkPersonalNumber} from "../utils_modules/personalNumbers";
import {createScrollToTop} from "./scroll-to-top";
import { initLeaveIntent } from "./leave-intent";
import {createPopupLauncher} from "./popup-launcher";

export function createGeneralPageEventsAndComponents() {
	// Load GTMIframe
	const gtmIFrame = expressQuerySelector<HTMLIFrameElement>(document, '.technical-loadGTMiframe', false);
	if (gtmIFrame)
		gtmIFrame.src = gtmIFrame.dataset.src;

	const leaveIntent = initLeaveIntent();

	// Load dataLayerIframe
	const dataLayerIframe = document.querySelector<HTMLElement>('#dataLayerIframe');
	if (!dataLayerIframe.getAttribute('data-cookieconsent')) {
		dataLayerIframe.setAttribute('data', dataLayerIframe.getAttribute('data-src'));
	}
	window.addEventListener("CookiebotOnAccept", function () {
		if (window.Cookiebot.consent.marketing) {
			const dataLayerIframe = document.querySelector('#dataLayerIframe');
			dataLayerIframe.setAttribute('data', dataLayerIframe.getAttribute('data-src'));

			if ((typeof window.enableLeaveIntentConfigValue === "undefined" || window.enableLeaveIntentConfigValue)) {
				leaveIntent.EnableLeaveIntent();
				leaveIntent.EnableLeaveIntentPopup();
			}
		}
	});

	// Set global props for the form validation (if we have forms)
	const forms = expressQuerySelectorAll(document, 'form');
	if (forms && forms.length > 0) {
		Pristine.addMessages((window as any).context.language, (window as any).validationMessages);
		Pristine.setLocale((window as any).context.language);
		Pristine.addValidator("regex", function (value, regexp) {
			return new RegExp(regexp).test(value);
		});
		Pristine.addValidator("urlsafe", function (value) {
			return isValidTextWithoutAngleBrackets(value);
		});
		Pristine.addValidator("blacklist", function (value, blacklist) {
			if (!blacklist)
				return true;
			const list = blacklist.split(',');
			for (let i = 0; i < list.length; i++)
				if (value.toLowerCase() === list[i])
					return false;

			return true;
		});
		Pristine.addValidator("emailrfc822", function (value) {
			return isValidEmailAddress(value);
		}, null, 98, true); // validation will be fired after required validation a stop if it fails. If we don't do this the normal email validation is also fired and we get two equal error messages
		Pristine.addValidator("personalnumber", function (value) {
			return checkPersonalNumber(value, this.dataset.regioncode || "");
		});
		Pristine.addValidator("usehousenumber", function (value, useHouseNumber) {
			if (!useHouseNumber) return true;
			const prefix = this.dataset.prefix || "";
			const houseNumberEl = expressQuerySelector<HTMLInputElement>(this.form, "input[name='" + prefix + "HouseNumber']", false);
			if (!houseNumberEl || !houseNumberEl.value) return true;
			const atStart = value.trim().substring(0, houseNumberEl.value.trim().length + 1) === houseNumberEl.value.trim() + ' ';
			const atEnd = value.trim().substring(value.trim().length - (houseNumberEl.value.trim().length + 1), value.trim().length) === ' ' + houseNumberEl.value.trim();
			return !atStart && !atEnd;
		});
		Pristine.addValidator("passwordcheck", function (value) {
			const passwordEl = expressQuerySelector<HTMLInputElement>(this.form, "input[name='password']", false);
			if (!passwordEl) return true;
			return value === passwordEl.value;
		});
		Pristine.addValidator("termsrequired", function (value) {
			return value;
		});
		Pristine.addValidator("invalidchars", function (value) {
			return isValidWithoutNonAlphaNumericCharsAtEnd(value, this as HTMLElement);
		});
		Pristine.addValidator("notonlynumbers", function (value) {
			return canNotContainOnlyNumerics(value);
		});
	}

	// Add click event to buttons with a data-url attribute
	const buttons = document.querySelectorAll('button[data-url]');
	buttons.forEach(el => {
		el.addEventListener('click', (ev) => {
			ev.preventDefault();
			window.location.href = el.getAttribute('data-url');
		});
	});

	// Set the isMobileDevice Cookie
	checkMobileAndSetCookie();

	// Initialize the header
	const openLeadTimesPopupAsync = () => {
		const initCollapsingPanels = (popup: HTMLElement) => {
			const collapsibleEls = expressQuerySelectorAll<HTMLElement>(popup, '.m-collapsable');
			collapsibleEls.forEach(el => {
				createCollapsable(el, {
					scrollIntoViewAfterOpen: false,
					titleElSelector: '.m-collapsable__title'
				});
			});
		};
		return createPopup(
			{
				view: "StyleguideLeadTimesDialog",
				dialogId: "lead-times-dialog",
				forceRefresh: true,
				onBeforeOpen: (popup) => initCollapsingPanels(popup)
			}
		)
			.then(popup => {
				return popup.openAsync();
			});
	};

	createMainNavigation(
		expressQuerySelector<HTMLElement>(document, ".technical-header", true),
		{
			getIconsAsync: urls => getInlineSvgsAsync(urls),
			openLeadTimesPopupAsync: openLeadTimesPopupAsync
		}
	);

	// Initialize the footer
	const footerCollapsibles = expressQuerySelectorAll(document, '.technical-footer-collapsable');
	footerCollapsibles.forEach(el => createCollapsable(<HTMLElement>el, {
		titleElSelector: '.technical-footer-collapsable-title',
		scrollIntoViewAfterOpen: false
	}));

	createNewsletterSubscribeAndUnsubscribe();

	window.addEventListener('error', logErrorEvent);

	// Init the country selection popup
	const bodyEl = expressQuerySelector<HTMLElement>(document, "body", true);
	const showCountrySelectionPopup = bodyEl.dataset.showcountryselection === "true" || false;
	const closeCountrySelectionPopup = () => {
		writeCookie("ShowCountrySelectionPopupAgainFrom", new Date().toISOString().slice(0, 10), 365);
	};

	const openCountrySelectionPopup = () => {
		return createPopup(
			{
				view: "StyleGuideCountrySelectionDialog",
				dialogId: "country-selection",
				forceRefresh: true,
				fromCache: false,
				onClose: closeCountrySelectionPopup,
				data: {path: window.location.pathname}
			}
		)
			.then(popup => {
				return popup.openAsync();
			});
	};

	if (showCountrySelectionPopup) {
		createCountrySelection({
			showCountrySelectionPopup: () => openCountrySelectionPopup(),
			close: () => closeCountrySelectionPopup(),
		});
	}

	// Create scroll to top
	createScrollToTop();

	// create popup launchers (links from cms with a content dialog parser)
	const popupLauncherEls = expressQuerySelectorAll<HTMLElement>(document, '.technical-popup-launcher');
	popupLauncherEls.forEach(el => createPopupLauncher(el));
}

const checkMobileAndSetCookie = () => {
	if (isTouchDevice() && (window.innerWidth < 500 || window.innerHeight < 500)) {
		if (getCookie("IsMobileDevice") !== "true")
			writeCookie("IsMobileDevice", "true", 365);
	} else {
		if (getCookie("IsMobileDevice") !== "false")
			writeCookie("IsMobileDevice", "false", 365);
	}
};

const isTouchDevice = () => ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
